import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { StatusBadge, useToast } from "@cx/ui";
import { variationGenerateInput, type ProductDetail, type VariationGenerateResult } from "@cx/contracts";
import { MAX_GRID_ATTRIBUTES, MAX_VARIATIONS, formatBRL } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

type Row = { name: string; options: string };
const splitOptions = (s: string) =>
  s
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);
const toRows = (d: ProductDetail): Row[] =>
  d.attributes.length
    ? d.attributes.map((a) => ({ name: a.name, options: a.options.join(", ") }))
    : [{ name: "", options: "" }];

/**
 * Aba Variações do pai (formato V): editor da grade (atributos e opções separadas por vírgula), prévia de combinações
 * e geração idempotente. Os filhos já criados ficam; os que saíram da grade aparecem como "Fora da grade".
 */
export function VariationsTab({
  detail,
  canGenerate,
  dirty,
}: {
  detail: ProductDetail;
  canGenerate: boolean;
  dirty: boolean;
}): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>(() => toRows(detail));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const parsed = rows.map((r) => splitOptions(r.options));
  const combos = parsed.every((o) => o.length > 0) ? parsed.reduce((n, o) => n * o.length, 1) : 0;
  const setRow = (i: number, patch: Partial<Row>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const generate = useMutation({
    mutationFn: (body: unknown) =>
      api<VariationGenerateResult>(`/products/${detail.id}/variations/generate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (r) => {
      setErrors({});
      const n = r.created.length;
      toast(
        "success",
        n === 0
          ? "Nenhuma variação nova: a grade já está completa."
          : n === 1
            ? "1 variação criada"
            : `${n} variações criadas`,
      );
      qc.invalidateQueries({ queryKey: ["product", companyId, detail.id] });
      qc.invalidateQueries({ queryKey: ["products", companyId] });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const onGenerate = () => {
    const body = { attributes: rows.map((r) => ({ name: r.name, options: splitOptions(r.options) })) };
    const r = variationGenerateInput.safeParse(body);
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const i of r.error.issues) e[i.path.join(".")] ??= i.message;
      setErrors(e);
      toast("danger", "Verifique a grade.");
      return;
    }
    generate.mutate(body);
  };
  const err = (path: string) => errors[path];

  return (
    <div>
      <h3 className="h6">Grade</h3>
      <p className="text-muted small">
        Informe os atributos (ex.: Cor, Tamanho) e as opções separadas por vírgula. Cada combinação vira uma
        variação com SKU próprio. Gerar de novo cria só as combinações que faltam.
      </p>
      {errors.attributes && (
        <div className="alert alert-danger py-2" role="alert">
          {errors.attributes}
        </div>
      )}
      {rows.map((r, i) => (
        <div className="row g-2 align-items-start mb-2" key={i}>
          <div className="col-md-3">
            <label className="form-label" htmlFor={`attr-name-${i}`}>
              Atributo {i + 1}
            </label>
            <input
              id={`attr-name-${i}`}
              className={`form-control${err(`attributes.${i}.name`) ? " is-invalid" : ""}`}
              aria-invalid={err(`attributes.${i}.name`) ? "true" : undefined}
              aria-describedby={err(`attributes.${i}.name`) ? `attr-name-${i}-err` : undefined}
              maxLength={30}
              value={r.name}
              disabled={!canGenerate}
              onChange={(e) => setRow(i, { name: e.target.value })}
            />
            {err(`attributes.${i}.name`) && (
              <div id={`attr-name-${i}-err`} className="invalid-feedback">
                {err(`attributes.${i}.name`)}
              </div>
            )}
          </div>
          <div className="col-md-7">
            <label className="form-label" htmlFor={`attr-options-${i}`}>
              Opções do atributo {i + 1}
            </label>
            <input
              id={`attr-options-${i}`}
              className={`form-control${err(`attributes.${i}.options`) ? " is-invalid" : ""}`}
              aria-invalid={err(`attributes.${i}.options`) ? "true" : undefined}
              aria-describedby={err(`attributes.${i}.options`) ? `attr-options-${i}-err` : undefined}
              placeholder="Ex.: Azul, Verde, Preto"
              value={r.options}
              disabled={!canGenerate}
              onChange={(e) => setRow(i, { options: e.target.value })}
            />
            {err(`attributes.${i}.options`) && (
              <div id={`attr-options-${i}-err`} className="invalid-feedback">
                {err(`attributes.${i}.options`)}
              </div>
            )}
          </div>
          <div className="col-md-2 d-flex align-items-end" style={{ minHeight: "4.1rem" }}>
            <button
              type="button"
              className="btn btn-outline-danger w-100"
              aria-label={`Remover atributo ${i + 1}`}
              disabled={!canGenerate || rows.length === 1}
              onClick={() => setRows(rows.filter((_, j) => j !== i))}
            >
              Remover
            </button>
          </div>
        </div>
      ))}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={!canGenerate || rows.length >= MAX_GRID_ATTRIBUTES}
          onClick={() => setRows([...rows, { name: "", options: "" }])}
        >
          Adicionar atributo
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canGenerate || dirty || generate.isPending}
          onClick={onGenerate}
        >
          Gerar variações
        </button>
        <span
          className={`small ${combos > MAX_VARIATIONS ? "text-danger" : "text-muted"}`}
          aria-live="polite"
        >
          {combos === 1 ? "1 combinação" : `${combos} combinações`} (máximo {MAX_VARIATIONS})
        </span>
      </div>
      {dirty && canGenerate && (
        <p className="text-warning-emphasis small">
          Salve as alterações do produto antes de gerar: as variações copiam os dados salvos.
        </p>
      )}
      {!canGenerate && (
        <p className="text-muted small">Gerar variações exige permissão para incluir e alterar produtos.</p>
      )}

      <h3 className="h6 mt-4">Variações ({detail.variations.length})</h3>
      {detail.variations.length === 0 ? (
        <p className="text-muted">Nenhuma variação gerada ainda.</p>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle" aria-label="Variações do produto">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">SKU</th>
                <th scope="col">Combinação</th>
                <th scope="col" className="text-end">
                  Preço
                </th>
                <th scope="col">Situação</th>
              </tr>
            </thead>
            <tbody>
              {detail.variations.map((v) => (
                <tr key={v.id}>
                  <td>
                    <Link to="/cadastros/produtos/$id" params={{ id: v.id }}>
                      {v.name}
                    </Link>
                  </td>
                  <td>{v.sku ?? "—"}</td>
                  <td>
                    {v.values.map((x) => `${x.attribute}: ${x.value}`).join(" · ")}
                    {!v.inGrid && (
                      <span className="ms-2">
                        <StatusBadge tone="warning">Fora da grade</StatusBadge>
                      </span>
                    )}
                  </td>
                  <td className="text-end money">{formatBRL(v.priceCents)}</td>
                  <td>
                    <StatusBadge tone={v.status === "active" ? "success" : "secondary"}>
                      {v.status === "active" ? "Ativo" : "Inativo"}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
